import Select from "react-select";
import DataTable, { Column } from "../../../components/DataTable";
import endpoints from "../../../config/endpoints";
import orderEnum from "../../../enums/orderEnum";
import restApi from "../../../services/restApi";
import tools from "../../../utils/tools";
import Status = orderEnum.Status;

const Products = ({ products, form, setForm, allProducts }) => {
  const isCallPrice = form.orderStatusSlug == Status.CallPrice;

  // Fetch full product details (with attributes) when a product is selected
  const fetchProductDetails = async (productId: number, orderProductId) => {
    const res = await restApi(endpoints.product.single + productId).get();
    if (res.code == 200) {
      setForm((prev) => {
        const cp = [...(prev.products || [])];
        const i = cp.findIndex((j) => j.id == orderProductId);
        if (i === -1) return prev;
        cp[i] = {
          ...cp[i],
          productId: res.data.id,
          product: res.data,
          attributeId: undefined,
          price: res.data.price || 0,
        };
        return { ...prev, products: cp };
      });
    }
  };

  // Build a human-readable label for an attribute from its specs
  const attributeLabel = (attribute) => {
    if (!attribute?.attributeSpecifics?.length) return `شاخص #${attribute.id}`;
    return attribute.attributeSpecifics
      .map(
        (as) =>
          `${as.specific?.title || as.specificSlug}: ${as.specificValue?.title || as.specificValueSlug}`,
      )
      .join(" | ");
  };

  const updateProduct = (orderProductId, patch: object) =>
    setForm((prev) => {
      const cp = [...(prev.products || [])];
      const i = cp.findIndex((j) => j.id == orderProductId);
      if (i === -1) return prev;
      cp[i] = { ...cp[i], ...patch };
      return { ...prev, products: cp };
    });

  // Pre-compute totalPrice so DataTable cells always reflect the latest value
  // even if the DataTable implementation memoises row objects internally.
  const enrichedProducts = (products || []).map((p) => ({
    ...p,
    _totalPrice: (Number(p.price) || 0) * (Number(p.count) || 1),
  }));

  const buildColumns = (): Column[] => {
    const cols: Column[] = [];

    // ── محصول ──────────────────────────────────────────
    cols.push({
      key: "product",
      title: "محصول",
      sortable: false,
      searchable: false,
      render: (_, orderProduct) => (
        <Select
          menuPosition="fixed"
          options={allProducts?.map((p) => ({
            value: p.id,
            label: p.title || p.code || `#${p.id}`,
          }))}
          value={
            orderProduct?.product
              ? {
                  value: orderProduct.product.id,
                  label:
                    orderProduct.product.title || orderProduct.product.code,
                }
              : null
          }
          className="orderInput"
          placeholder="انتخاب محصول"
          onChange={(selected) =>
            fetchProductDetails(selected.value, orderProduct.id)
          }
        />
      ),
    });

    // ── شاخص (attribute selector) ──────────────────────
    cols.push({
      key: "attributeId",
      title: "شاخص",
      sortable: false,
      searchable: false,
      render: (_, orderProduct) => {
        // product.attributes comes from the single-product fetch.
        // In edit mode the order/single endpoint may not load them yet —
        // fall back to the already-loaded orderProduct.attribute (singular)
        // so the selected value is at least visible.
        const attributes: any[] =
          orderProduct?.product?.attributes ||
          (orderProduct?.attribute ? [orderProduct.attribute] : []);

        const selectedId =
          orderProduct.attributeId ?? orderProduct?.attribute?.id;

        const selected = attributes.find((a) => a.id == selectedId);

        return (
          <Select
            menuPosition="fixed"
            options={attributes.map((a) => ({
              value: a.id,
              label: attributeLabel(a),
            }))}
            value={
              selected
                ? { value: selected.id, label: attributeLabel(selected) }
                : null
            }
            placeholder={attributes.length ? "انتخاب شاخص" : "ابتدا محصول انتخاب کنید"}
            className="orderInput"
            isDisabled={!attributes.length}
            onChange={(opt) => {
              const attr = attributes.find((a) => a.id == opt.value);
              updateProduct(orderProduct.id, {
                attributeId: opt.value,
                price: attr?.price ?? orderProduct.price,
              });
            }}
          />
        );
      },
    });

    // ── SKU ────────────────────────────────────────────
    cols.push({
      key: "product.code",
      title: "SKU",
      sortable: false,
      searchable: false,
      render: (_, orderProduct) => (
        <span className="textAlignCenter">{orderProduct?.product?.code}</span>
      ),
    });

    // ── تعداد ──────────────────────────────────────────
    cols.push({
      key: "count",
      title: "تعداد",
      sortable: false,
      searchable: false,
      render: (_, orderProduct) => (
        <div className="quantityButtom">
          <i
            className="tablePlusIcon"
            onClick={() =>
              updateProduct(orderProduct.id, {
                count: (Number(orderProduct.count) || 1) + 1,
              })
            }
          />
          <input
            className="quantityNumber"
            value={orderProduct.count}
            onChange={(e) =>
              updateProduct(orderProduct.id, {
                count: Number(e.target.value) || 1,
              })
            }
          />
          <i
            className="tableCollapsIcon"
            onClick={() =>
              updateProduct(orderProduct.id, {
                count: Math.max(1, (Number(orderProduct.count) || 1) - 1),
              })
            }
          />
        </div>
      ),
    });

    // ── قیمت واحد (editable, formatted) ───────────────
    cols.push({
      key: "price",
      title: "قیمت واحد",
      sortable: false,
      searchable: false,
      render: (_, orderProduct) => (
        <input
          className="persianName"
          value={tools.formatPriceToStr(Number(orderProduct?.price) || 0)}
          onChange={(e) =>
            updateProduct(orderProduct.id, {
              price: tools.formatStrToPrice(e.target.value),
            })
          }
        />
      ),
    });

    // ── قیمت کل — reads the pre-computed _totalPrice ──
    cols.push({
      key: "_totalPrice",
      title: "قیمت کل",
      sortable: false,
      searchable: false,
      render: (value) => (
        <span className="priceHolder backGround1">
          {tools.formatPrice(value || 0)}
        </span>
      ),
    });

    // ── تایید مشتری (CallPrice only) ───────────────────
    if (isCallPrice) {
      cols.push({
        key: "variationId",
        title: "تایید مشتری",
        sortable: false,
        searchable: false,
        render: (_, orderProduct) => {
          const options = [
            { value: 0, label: "انتخاب کنید" },
            { value: -1, label: "عدم تایید" },
            ...(orderProduct?.variations || []).map((variant) => ({
              value: variant.id,
              label: `${tools.formatPrice(variant?.sellPrice || 0)} - ${variant.brand?.title || "—"}`,
            })),
          ];

          return (
            <Select
              options={options}
              value={options.find((e) => e.value == orderProduct.variationId)}
              className="width100 dirRtl"
              // the cell is narrow, so the menu escapes the table instead of
              // being clipped by it
              menuPortalTarget={document.body}
              styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
              onChange={(selected) =>
                updateProduct(orderProduct.id, { variationId: selected.value })
              }
            />
          );
        },
      });
    }

    // ── عملیات (delete / open on the storefront) ───────
    cols.push({
      key: "id",
      title: "عملیات",
      sortable: false,
      searchable: false,
      render: (_, orderProduct) => (
        <div className="tableActions">
          <i
            className="trash clickable"
            onClick={() =>
              setForm((prev) => ({
                ...prev,
                products: prev.products.filter((e) => e.id !== orderProduct.id),
              }))
            }
          />
          {/* a row whose product has not been picked yet has nothing to open */}
          {orderProduct?.product?.slug && (
            <a
              href={`${process.env.REACT_APP_CLIENT_URL}/product/${orderProduct.product.slug}`}
              target="_blank"
              rel="noreferrer"
              title="مشاهده محصول"
            >
              <i className="eye clickable" />
            </a>
          )}
        </div>
      ),
    });

    return cols;
  };

  return (
    <section className="bottom">
      <h6 className="dashBoardTitle">محصولات</h6>
      <DataTable
        columns={buildColumns()}
        data={enrichedProducts}
        rowKey="id"
        pageSize={50}
      />
      <div className="addProductTr">
        <span
          className="addProductButton"
          onClick={() =>
            setForm((prev) => {
              const cp = [...(prev?.products || [])];
              cp.push({
                count: 1,
                price: 0,
                id: Date.now(),
                isNew: true,
              });
              return { ...prev, products: cp };
            })
          }
        >
          اضافه کردن محصول
        </span>
      </div>
    </section>
  );
};

export default Products;
